import type {
  AiUsageEvent,
  AiUsageSummary,
  Application,
  LogWithRelations,
  MessageTypeWithStats,
  OverviewMetrics,
} from '@owl/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../lib/api';
import { computeAppHealth } from '../../../lib/appHealth';
import { rangeIso, timeAgo } from '../../../lib/time';
import { useLoader } from '../../../lib/useLoader';
import { formatInt, formatSeconds, formatUsd } from '../../charts/AiUsageBlocks';
import { DonutChart } from '../../charts/DonutChart';
import { readHiddenTimelineTypes } from '../../charts/StackedTimeline';
import { Icon } from '../../icons';
import { MessageTypeChip } from '../../shared';
import type { AppDashTab, MobileShellCtx } from '../MobileShellCtx';
import { ActivityBars, formatCompact, formatPct } from '../charts';

const FILTER_STORAGE_KEY = 'owl.m.logFilter';

type LogFilter = {
  messageTypeIds: string[];
  search: string;
};

function loadFilter(): LogFilter {
  if (typeof window === 'undefined') return { messageTypeIds: [], search: '' };
  try {
    const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return { messageTypeIds: [], search: '' };
    const parsed = JSON.parse(raw) as Partial<LogFilter>;
    return {
      messageTypeIds: parsed.messageTypeIds ?? [],
      search: parsed.search ?? '',
    };
  } catch {
    return { messageTypeIds: [], search: '' };
  }
}

function initials(value: string): string {
  return value.slice(0, 2).toUpperCase();
}

export function AppDashboardPage({
  ctx,
  applicationId,
  tab,
  focusLogId,
}: {
  ctx: MobileShellCtx;
  applicationId: string;
  tab: AppDashTab;
  focusLogId?: string;
}) {
  const [app, setApp] = useState<Application | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mts, setMts] = useState<MessageTypeWithStats[]>([]);

  const load = useCallback(
    async (isCancelled: () => boolean) => {
      try {
        const [a, t] = await Promise.all([
          api.getApplication(applicationId),
          api.listMessageTypes(),
        ]);
        if (isCancelled()) return;
        setApp(a.application);
        setMts(t.items);
      } catch (e) {
        if (!isCancelled()) setErr((e as Error).message);
      }
    },
    [applicationId],
  );

  useLoader(load, ctx.refreshTick);

  const setTab = (next: AppDashTab) => ctx.navigate({ name: 'appdash', applicationId, tab: next });

  return (
    <div className="m-page">
      {/* back header + app identity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" className="m-iconbtn" onClick={ctx.goBack} aria-label="Volver">
          <Icon.ChevronLeft size={16} />
        </button>
        <div className="app-avatar" style={{ width: 40, height: 40, borderRadius: 12 }}>
          {app ? initials(app.product) : '··'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 18,
              fontWeight: 500,
              letterSpacing: '-0.01em',
            }}
          >
            {app && <span className={`dot health-${computeAppHealth(app)}`} />}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {app ? app.name : 'Application'}
            </span>
          </div>
          {app && (
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: 'var(--fg-4)',
                marginTop: 2,
                letterSpacing: '0.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {app.product} · {app.company}
              {app.instance ? ` · ${app.instance}` : ''} ·{' '}
              {app.lastLogAt ? `último log ${timeAgo(app.lastLogAt)}` : 'sin logs'}
            </div>
          )}
        </div>
      </div>

      {app && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span className="chip">{app.environment}</span>
          <span className="chip">{app.status}</span>
        </div>
      )}

      {err && <div className="m-error-banner">{err}</div>}
      {!app && !err && <div className="m-loading">Cargando…</div>}

      {/* segmented sub-tabs */}
      <div className="m-tabs">
        <button
          type="button"
          className={`m-tab ${tab === 'logs' ? 'active' : ''}`}
          onClick={() => setTab('logs')}
        >
          Logs
        </button>
        <button
          type="button"
          className={`m-tab ${tab === 'activity' ? 'active' : ''}`}
          onClick={() => setTab('activity')}
        >
          Actividad
        </button>
        <button
          type="button"
          className={`m-tab ${tab === 'ai' ? 'active' : ''}`}
          onClick={() => setTab('ai')}
        >
          AI
        </button>
        <button
          type="button"
          className={`m-tab ${tab === 'info' ? 'active' : ''}`}
          onClick={() => setTab('info')}
        >
          Info
        </button>
      </div>

      {tab === 'logs' && (
        <LogsTab ctx={ctx} applicationId={applicationId} mts={mts} focusLogId={focusLogId} />
      )}
      {tab === 'activity' && <ActivityTab ctx={ctx} applicationId={applicationId} mts={mts} />}
      {tab === 'ai' && <AITab ctx={ctx} applicationId={applicationId} />}
      {tab === 'info' && app && <InfoTab app={app} />}
    </div>
  );
}

function LogsTab({
  ctx,
  applicationId,
  mts,
  focusLogId,
}: {
  ctx: MobileShellCtx;
  applicationId: string;
  mts: MessageTypeWithStats[];
  focusLogId?: string;
}) {
  const [filter, setFilterState] = useState<LogFilter>(loadFilter);
  const [logs, setLogs] = useState<LogWithRelations[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filter));
    } catch {
      // ignore
    }
  }, [filter]);

  const loadLogs = useCallback(
    async (isCancelled: () => boolean) => {
      setLoading(true);
      setErr(null);
      try {
        if (focusLogId) {
          // Arriving from a notification: page the feed from the alert's log backwards,
          // so the line that fired sits at the top with what led up to it below.
          //
          // The normal query cannot be reused. It returns the newest 50 rows, and a busy
          // app outruns that within minutes of the alert. It also applies this device's
          // saved environment and filters, either of which can exclude the very log the
          // notification was about. The application id already pins the environment.
          const { log } = await api.getLog(focusLogId);
          const r = await api.listLogs({ applicationId, to: log.timestamp, limit: 50 });
          if (isCancelled()) return;
          setLogs(r.items);
          setCursor(r.nextCursor);
          return;
        }
        const { from, to } = rangeIso(ctx.range);
        const r = await api.listLogs({
          applicationId,
          environment: ctx.environment,
          messageTypeIds: filter.messageTypeIds.length > 0 ? filter.messageTypeIds : undefined,
          search: filter.search || undefined,
          from,
          to,
          limit: 50,
        });
        if (isCancelled()) return;
        setLogs(r.items);
        setCursor(r.nextCursor);
      } catch (e) {
        if (!isCancelled()) setErr((e as Error).message);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [applicationId, ctx.environment, ctx.range, filter.messageTypeIds, filter.search, focusLogId],
  );

  useLoader(loadLogs, ctx.refreshTick);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      if (focusLogId) {
        const r = await api.listLogs({ applicationId, limit: 50, cursor });
        setLogs((prev) => [...prev, ...r.items]);
        setCursor(r.nextCursor);
        return;
      }
      const { from, to } = rangeIso(ctx.range);
      const r = await api.listLogs({
        applicationId,
        environment: ctx.environment,
        messageTypeIds: filter.messageTypeIds.length > 0 ? filter.messageTypeIds : undefined,
        search: filter.search || undefined,
        from,
        to,
        limit: 50,
        cursor,
      });
      setLogs((prev) => [...prev, ...r.items]);
      setCursor(r.nextCursor);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoadingMore(false);
    }
  };

  const focusedRowRef = useRef<HTMLButtonElement | null>(null);
  const scrolledToRef = useRef<string | null>(null);
  useEffect(() => {
    // The focused row only exists once its page has rendered, so this has to re-check
    // whenever the feed changes; the ref itself carries no dependency React can see.
    void logs;
    if (!focusLogId || scrolledToRef.current === focusLogId) return;
    const row = focusedRowRef.current;
    if (!row) return;
    // Once per target: the 30s auto-refresh re-renders the feed, and yanking the user
    // back to the line every half minute would make it impossible to read around it.
    scrolledToRef.current = focusLogId;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focusLogId, logs]);

  // Filter sheet shares state via a global ref the FilterSheet reads.
  useEffect(() => {
    window.__mobileFilter = {
      mts,
      filter,
      setFilter: setFilterState,
    };
    return () => {
      window.__mobileFilter = undefined;
    };
  }, [mts, filter]);

  const toggleMt = (id: string) =>
    setFilterState((f) => ({
      ...f,
      messageTypeIds: f.messageTypeIds.includes(id)
        ? f.messageTypeIds.filter((x) => x !== id)
        : [...f.messageTypeIds, id],
    }));

  const activeFilterCount = filter.messageTypeIds.length + (filter.search ? 1 : 0);

  return (
    <>
      {/* search + filter sheet */}
      <div style={{ display: 'flex', gap: 8 }}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--bg-2)',
            border: '1px solid var(--border-1)',
            borderRadius: 'var(--radius-input)',
            padding: '0 10px',
            color: 'var(--fg-3)',
          }}
        >
          <Icon.Search size={14} />
          <input
            value={filter.search}
            onChange={(e) => setFilterState((f) => ({ ...f, search: e.target.value }))}
            placeholder="Buscar en mensajes…"
            style={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'var(--fg-1)',
              fontSize: 13,
              padding: '10px 0',
            }}
          />
          {filter.search && (
            <button
              type="button"
              onClick={() => setFilterState((f) => ({ ...f, search: '' }))}
              aria-label="Limpiar"
              style={{ color: 'var(--fg-4)', display: 'grid', placeItems: 'center' }}
            >
              <Icon.X size={14} />
            </button>
          )}
        </div>
        <button
          type="button"
          className="m-iconbtn"
          onClick={() => ctx.openSheet({ kind: 'filter' })}
          aria-label="Filtros"
        >
          <Icon.Filter size={16} />
          {activeFilterCount > 0 && <span className="badge">{activeFilterCount}</span>}
        </button>
      </div>

      {/* message-type filter chips */}
      {mts.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            paddingBottom: 2,
          }}
        >
          <button
            type="button"
            className={`m-range-chip ${filter.messageTypeIds.length === 0 ? 'active' : ''}`}
            onClick={() => setFilterState((f) => ({ ...f, messageTypeIds: [] }))}
          >
            Todos
          </button>
          {mts.map((mt) => {
            const on = filter.messageTypeIds.includes(mt.id);
            return (
              <button
                type="button"
                key={mt.id}
                className={`m-range-chip ${on ? 'active' : ''}`}
                onClick={() => toggleMt(mt.id)}
                style={
                  on
                    ? { borderColor: `${mt.color}55`, color: mt.color, background: `${mt.color}1A` }
                    : undefined
                }
              >
                {mt.name}
              </button>
            );
          })}
        </div>
      )}

      {focusLogId && (
        <div className="m-focus-banner">
          <span>Centrado en el log de la alerta · sin filtros</span>
          <button
            type="button"
            onClick={() => ctx.navigate({ name: 'appdash', applicationId, tab: 'logs' })}
          >
            Ver recientes
          </button>
        </div>
      )}
      {err && <div className="m-error-banner">{err}</div>}
      {loading && <div className="m-loading">Cargando logs…</div>}

      {!loading && logs.length === 0 && <div className="m-empty">No hay logs</div>}

      {logs.length > 0 && (
        <section className="log-feed-m">
          <div className="head">
            <div className="t">Feed</div>
            <div className="c">{logs.length} eventos</div>
          </div>
          <div className="log-list-m">
            {logs.map((l) => (
              <button
                key={l.id}
                ref={l.id === focusLogId ? focusedRowRef : undefined}
                type="button"
                className={l.id === focusLogId ? 'log-row-m focused' : 'log-row-m'}
                onClick={() => ctx.openSheet({ kind: 'log', logId: l.id })}
              >
                <span className="bar" style={{ background: l.messageType.color }} />
                <div className="body">
                  <div className="top">
                    <MessageTypeChip mt={l.messageType} dense />
                    <span className="time">
                      {new Date(l.timestamp).toLocaleTimeString('es-ES', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </span>
                    <span className="app">{timeAgo(l.timestamp)}</span>
                  </div>
                  <div className="msg">{l.message}</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {cursor && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          style={{
            padding: 12,
            border: '1px solid var(--border-1)',
            background: 'var(--bg-2)',
            borderRadius: 'var(--radius-card)',
            color: 'var(--fg-2)',
            fontSize: 13,
            textAlign: 'center',
          }}
        >
          {loadingMore ? 'Cargando…' : 'Cargar más'}
        </button>
      )}
    </>
  );
}

function ActivityTab({
  ctx,
  applicationId,
  mts,
}: {
  ctx: MobileShellCtx;
  applicationId: string;
  mts: MessageTypeWithStats[];
}) {
  const [metrics, setMetrics] = useState<
    (OverviewMetrics & { avgLatency: number | null; p95Latency: number | null }) | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const activityRange = useMemo(() => rangeIso(ctx.range), [ctx.range]);

  const load2 = useCallback(
    async (isCancelled: () => boolean) => {
      setLoading(true);
      setErr(null);
      try {
        const { from, to } = rangeIso(ctx.range);
        const r = await api.applicationMetrics(applicationId, from, to);
        if (!isCancelled()) setMetrics(r);
      } catch (e) {
        if (!isCancelled()) setErr((e as Error).message);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [applicationId, ctx.range],
  );

  useLoader(load2, ctx.refreshTick);

  if (loading && !metrics) return <div className="m-loading">Cargando…</div>;
  if (err) return <div className="m-error-banner">{err}</div>;
  if (!metrics) return null;

  const health = Math.round((1 - metrics.totals.errorRate) * 100);
  // Same message types the desktop timeline legend hides are hidden here too.
  const hiddenNames = readHiddenTimelineTypes();
  const byId = new Map(mts.map((m) => [m.id, m]));
  const distribution = metrics.distribution
    .map((d) => {
      const mt = byId.get(d.messageTypeId);
      if (!mt || hiddenNames.has(mt.name)) return null;
      return { value: d.count, color: mt.color, label: mt.name };
    })
    .filter((d): d is { value: number; color: string; label: string } => d !== null)
    .sort((a, b) => b.value - a.value);
  const distributionTotal = distribution.reduce((sum, d) => sum + d.value, 0);

  return (
    <>
      <div className="kpi-row-mobile">
        <div className="kpi-tile">
          <div className="l">
            <Icon.Activity size={11} />
            Logs · {ctx.range}
          </div>
          <div className="v">{formatCompact(metrics.totals.logs)}</div>
          <div className="d">Lifetime {formatCompact(metrics.lifetime.totalLogs)}</div>
        </div>
        <div className="kpi-tile">
          <div className="l">
            <Icon.Bolt size={11} />
            App health
          </div>
          <div
            className="v"
            style={{ color: health >= 95 ? undefined : health >= 85 ? '#f5c373' : '#ff9a9a' }}
          >
            {health}
            <small>%</small>
          </div>
          <div className="d">Uptime {formatPct(metrics.totals.uptime)}</div>
        </div>
        <div className="kpi-tile">
          <div className="l">
            <Icon.Alert size={11} />
            Error rate
          </div>
          <div className="v">
            {formatPct(metrics.totals.errorRate)}
            <small>err</small>
          </div>
          <div className="d">
            Latencia {metrics.avgLatency != null ? `${metrics.avgLatency.toFixed(0)}ms` : '—'}
          </div>
        </div>
        <div className="kpi-tile">
          <div className="l">
            <Icon.Bell size={11} />
            Alertas
          </div>
          <div
            className="v"
            style={{ color: metrics.totals.alertsTriggered > 0 ? '#ff8b8b' : undefined }}
          >
            {metrics.totals.alertsTriggered}
          </div>
          <div className="d">
            p95 {metrics.p95Latency != null ? `${metrics.p95Latency.toFixed(0)}ms` : '—'}
          </div>
        </div>
      </div>

      <section className="m-card">
        <div className="m-card-h">
          <div className="m-card-t">Actividad</div>
          <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-4)' }}>
            {ctx.range}
          </div>
        </div>
        <div className="m-card-body" style={{ paddingTop: 0 }}>
          <ActivityBars
            timeline={metrics.timeline}
            messageTypes={mts}
            from={activityRange.from}
            to={activityRange.to}
            onBucketSelect={({ fromMs, size }) =>
              ctx.openSheet({ kind: 'bucketLogs', fromMs, size, applicationId })
            }
          />
        </div>
      </section>

      <section className="m-card">
        <div className="m-card-h">
          <div className="m-card-t">Distribución</div>
          <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-4)' }}>
            {ctx.range}
          </div>
        </div>
        <div className="m-card-body m-donut-block">
          {distribution.length === 0 ? (
            <div className="m-empty">Sin logs en este rango</div>
          ) : (
            <>
              <DonutChart
                data={distribution}
                size={116}
                thickness={15}
                centerLabel={`LOGS · ${ctx.range.toUpperCase()}`}
                centerValue={formatCompact(distributionTotal)}
              />
              <div className="m-donut-legend">
                {distribution.map((d) => (
                  <div key={d.label} className="row">
                    <span className="sq" style={{ background: d.color }} />
                    <span className="mono name">{d.label}</span>
                    <span className="mono val">{d.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

function unitChip(unit: 'tokens' | 'audio_seconds' | 'characters' | undefined, ev: AiUsageEvent) {
  if (unit === 'audio_seconds') {
    return (
      <>
        <span className="unit-chip audio">audio</span>
        <span style={{ marginLeft: 8 }}>{formatSeconds(ev.audioSeconds)}</span>
      </>
    );
  }
  if (unit === 'characters') {
    return (
      <>
        <span className="unit-chip chars">chars</span>
        <span style={{ marginLeft: 8 }}>{formatInt(ev.characters)}</span>
      </>
    );
  }
  return (
    <>
      <span className="unit-chip tokens">tokens</span>
      <span style={{ marginLeft: 8 }}>
        {formatInt(ev.inputTokens)} in · {formatInt(ev.outputTokens)} out
      </span>
    </>
  );
}

function AITab({ ctx, applicationId }: { ctx: MobileShellCtx; applicationId: string }) {
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [events, setEvents] = useState<AiUsageEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const aiRange = useMemo(() => {
    if (ctx.range === '7d') return '7d' as const;
    if (ctx.range === '30d') return '30d' as const;
    if (ctx.range === 'mtd') return 'mtd' as const;
    return '24h' as const;
  }, [ctx.range]);

  const load3 = useCallback(
    async (isCancelled: () => boolean) => {
      setLoading(true);
      setErr(null);
      try {
        const [s, e] = await Promise.all([
          api.aiUsageSummary({ range: aiRange, applicationId, environment: ctx.environment }),
          api.listAiUsageEvents({
            range: aiRange,
            applicationId,
            environment: ctx.environment,
            limit: 30,
          }),
        ]);
        if (isCancelled()) return;
        setSummary(s);
        setEvents(e.items);
      } catch (ex) {
        if (!isCancelled()) setErr((ex as Error).message);
      } finally {
        if (!isCancelled()) setLoading(false);
      }
    },
    [aiRange, applicationId, ctx.environment],
  );

  useLoader(load3, ctx.refreshTick);

  const modelIndex = useMemo(() => {
    const m = new Map<string, AiUsageSummary['byModel'][number]>();
    if (summary) for (const row of summary.byModel) m.set(`${row.provider}/${row.model}`, row);
    return m;
  }, [summary]);

  if (loading && !summary) return <div className="m-loading">Cargando…</div>;
  if (err) return <div className="m-error-banner">{err}</div>;
  if (!summary) return null;

  const t = summary.totals;
  const topModels = [...summary.byModel].sort((a, b) => b.costUsd - a.costUsd).slice(0, 6);

  return (
    <>
      <div className="kpi-row-mobile">
        <div className="kpi-tile">
          <div className="l">
            <Icon.Coin size={11} />
            Coste · {aiRange}
          </div>
          <div className="v">{formatUsd(t.costUsd)}</div>
          <div className="d">{formatInt(t.events)} eventos</div>
        </div>
        <div className="kpi-tile">
          <div className="l">
            <Icon.Activity size={11} />
            Tokens
          </div>
          <div className="v">{formatInt(t.inputTokens + t.outputTokens)}</div>
          <div className="d">In {formatInt(t.inputTokens)}</div>
        </div>
        <div className="kpi-tile">
          <div className="l">
            <Icon.Activity size={11} />
            Audio
          </div>
          <div className="v">{formatSeconds(t.audioSeconds)}</div>
          <div className="d">Chars {formatInt(t.characters)}</div>
        </div>
        <div className="kpi-tile">
          <div className="l">
            <Icon.Layers size={11} />
            Modelos
          </div>
          <div className="v">{summary.byModel.length}</div>
          <div className="d">Out {formatInt(t.outputTokens)}</div>
        </div>
      </div>

      {topModels.length > 0 && (
        <>
          <div className="m-section-h">
            <span className="m-section-t">Modelos usados</span>
          </div>
          <section className="m-card">
            {topModels.map((m) => {
              const pct = t.costUsd > 0 ? (m.costUsd / t.costUsd) * 100 : 0;
              return (
                <div
                  key={`${m.provider}/${m.model}`}
                  className="rank-row"
                  style={{ cursor: 'default' }}
                >
                  <span className="swatch" style={{ background: m.color }} />
                  <div className="info">
                    <div className="name">{m.displayLabel}</div>
                    <div className="meta">
                      <span>{m.provider}</span>
                      <span className="sep">·</span>
                      <span>{pct.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="right">
                    <div className="cost">{formatUsd(m.costUsd)}</div>
                    <div className="pct">{formatInt(m.events)} ev</div>
                  </div>
                </div>
              );
            })}
          </section>
        </>
      )}

      <div className="m-section-h">
        <span className="m-section-t">Últimos eventos</span>
      </div>
      <section className="m-card">
        {events.length === 0 && <div className="m-empty">Sin eventos AI</div>}
        {events.map((ev, idx) => {
          const model = modelIndex.get(`${ev.provider}/${ev.model}`);
          return (
            <button
              key={`${ev.timestamp}-${idx}`}
              type="button"
              className="ai-event"
              onClick={() => {
                window.__mobileAiEvents = events;
                ctx.openSheet({ kind: 'aiEvent', eventIdx: idx });
              }}
            >
              <span className="bar" style={{ background: model?.color ?? 'var(--fg-3)' }} />
              <div className="body">
                <div className="top">
                  <span className="model">{model?.displayLabel ?? ev.model}</span>
                  <span className="prov">· {ev.provider}</span>
                  <span className="time">{timeAgo(ev.timestamp)}</span>
                </div>
                <div className="units">{unitChip(model?.pricingUnit, ev)}</div>
              </div>
              <div className="cost">{formatUsd(ev.costUsd ?? 0)}</div>
            </button>
          );
        })}
      </section>
    </>
  );
}

function InfoTab({ app }: { app: Application }) {
  return (
    <>
      <div className="m-section-h">
        <span className="m-section-t">Token de ingest</span>
      </div>
      <div className="token-badge-m">
        <span className="t">{app.tokenPrefix}</span>
      </div>

      <div className="m-section-h">
        <span className="m-section-t">Detalles</span>
      </div>
      <div className="kv-block">
        <div className="kv-row">
          <span className="k">nombre</span>
          <span className="v">{app.name}</span>
        </div>
        <div className="kv-row">
          <span className="k">product</span>
          <span className="v">{app.product}</span>
        </div>
        <div className="kv-row">
          <span className="k">company</span>
          <span className="v">{app.company}</span>
        </div>
        {app.instance && (
          <div className="kv-row">
            <span className="k">instance</span>
            <span className="v">{app.instance}</span>
          </div>
        )}
        <div className="kv-row">
          <span className="k">environment</span>
          <span className="v">{app.environment}</span>
        </div>
        <div className="kv-row">
          <span className="k">estado</span>
          <span
            className="v"
            style={{
              color:
                app.status === 'active'
                  ? '#92d3a3'
                  : app.status === 'paused'
                    ? '#f5c373'
                    : 'var(--fg-3)',
            }}
          >
            {app.status}
          </span>
        </div>
        <div className="kv-row">
          <span className="k">último log</span>
          <span className="v">{app.lastLogAt ? timeAgo(app.lastLogAt) : '—'}</span>
        </div>
        <div className="kv-row">
          <span className="k">creado</span>
          <span className="v">{new Date(app.createdAt).toLocaleString('es-ES')}</span>
        </div>
        <div className="kv-row">
          <span className="k">id</span>
          <span className="v">{app.id}</span>
        </div>
      </div>
    </>
  );
}

declare global {
  interface Window {
    __mobileFilter?: {
      mts: MessageTypeWithStats[];
      filter: LogFilter;
      setFilter: (updater: LogFilter | ((prev: LogFilter) => LogFilter)) => void;
    };
  }
}
